import { qoursesApi } from '@/api/qourses.tsx'
import Dynamic from '@/components/modals/dynamic.tsx'
import { popAllModals } from '@/components/modals/index.tsx'
import { GetEndCustomerMultipassesQueryKey } from '@/hooks/customer/useGetEndCustomerMultipasses.tsx'
import useGetCustomerBookingRevocationEligibility from '@/hooks/public/useGetCustomerBookingRevocationEligibility.tsx'
import { GetOrderBookingsPublicQueryKey } from '@/hooks/public/useGetOrderBookingsPublic.tsx'
import { Button } from '@/shadcn/components/ui/button.tsx'
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shadcn/components/ui/dialog.tsx'
import { Skeleton } from '@/shadcn/components/ui/skeleton.tsx'
import { ToastVariant, minDelay, sendNotification } from '@/utils.tsx'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export default function CustomerRevokeBookingModal({
  bookingId,
  orderId,
}: {
  bookingId: string
  orderId: string
}) {
  const { t: translate } = useTranslation()
  const queryClient = useQueryClient()

  const [submitting, setSubmitting] = useState(false)

  const { bookingRevocationEligibility, isLoading, isError } =
    useGetCustomerBookingRevocationEligibility(bookingId)

  if (isError) {
    return (
      <Dynamic.Content>
        <DialogHeader>
          <DialogTitle>
            {translate('modals.customerRevokeBookingModal.errorState.title')}
          </DialogTitle>
          <DialogDescription>
            {translate('modals.customerRevokeBookingModal.errorState.subtitle')}
          </DialogDescription>
        </DialogHeader>
      </Dynamic.Content>
    )
  }

  if (isLoading) {
    return (
      <Dynamic.Content>
        <DialogHeader>
          <DialogTitle>
            <Skeleton className="h-5 w-3/4" />
          </DialogTitle>
          <DialogDescription className="space-y-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-full" />
          </DialogDescription>
        </DialogHeader>
      </Dynamic.Content>
    )
  }

  async function handleRevoke() {
    try {
      setSubmitting(true)
      await minDelay(
        qoursesApi.paymentCustomer.paymentCustomerControllerRevokeBookingIfEligibleV1(bookingId),
        800,
      )
      sendNotification(
        translate('modals.customerRevokeBookingModal.notification.title'),
        translate('modals.customerRevokeBookingModal.notification.subtitle'),
        ToastVariant.Success,
      )
    } catch (error) {
      console.error(error)
    } finally {
      setSubmitting(false)
      popAllModals()
      queryClient.invalidateQueries(GetOrderBookingsPublicQueryKey(orderId))
      // Also refetch multipasses to avoid showing wrong multipass count if user navigates back
      queryClient.invalidateQueries(GetEndCustomerMultipassesQueryKey())
    }
  }

  const revocableUntil = bookingRevocationEligibility.revocableUntil
    ? DateTime.fromISO(bookingRevocationEligibility.revocableUntil).toLocaleString(
        DateTime.DATETIME_MED,
      )
    : null
  const revocationBlockedAt = bookingRevocationEligibility.revocationBlockedAt
    ? DateTime.fromISO(bookingRevocationEligibility.revocationBlockedAt).toLocaleString(
        DateTime.DATETIME_MED,
      )
    : null

  const descriptionKey = bookingRevocationEligibility.revocable
    ? bookingRevocationEligibility.refundRequired
      ? 'revocableRefunded'
      : 'revocableNotNecessary'
    : 'notRevocable'

  return (
    <Dynamic.Content className="p-4 sm:p-8">
      <DialogHeader className="mb-4">
        <DialogTitle className="mt-6 sm:mt-2">
          {translate(`modals.customerRevokeBookingModal.${descriptionKey}.title`)}
        </DialogTitle>
        <DialogDescription className="space-y-2">
          <p>{translate(`modals.customerRevokeBookingModal.${descriptionKey}.subtitle`)}</p>
          {!!revocableUntil && bookingRevocationEligibility.revocable && (
            <p>
              {translate('modals.customerRevokeBookingModal.revocableUntil', {
                date: revocableUntil,
              })}
            </p>
          )}
          {!!revocationBlockedAt && !bookingRevocationEligibility.revocable && (
            <p>
              {translate('modals.customerRevokeBookingModal.revocationBlockedAt', {
                date: revocationBlockedAt,
              })}
            </p>
          )}
          {!!bookingRevocationEligibility.revocationBlockedReason &&
            !bookingRevocationEligibility.revocable && (
              <p>
                {translate(
                  'modals.customerRevokeBookingModal.blockedReasons.' +
                    bookingRevocationEligibility.revocationBlockedReason,
                )}
              </p>
            )}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button
          variant="indigo"
          disabled={submitting || !bookingRevocationEligibility.revocable}
          onClick={handleRevoke}
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {translate('common.loading')}
            </>
          ) : (
            translate('modals.customerRevokeBookingModal.revokeButton')
          )}
        </Button>
      </DialogFooter>
    </Dynamic.Content>
  )
}
